<script setup lang="ts">
import BaseTextBlock from '~/components/BaseTextBlock.vue';
import BaseButton from '~/components/BaseButton.vue';
import { useRouter, useRoute } from 'vue-router';
import { LANG } from '~/config/lang';
import { useApp } from '~/store/useApp';
import { IGetVacancy } from '~/types/api';
import BasePopup from '~/components/BasePopup.vue';

const config = useRuntimeConfig();
const appStore = useApp();
const route = useRoute();

function applyHandle() {
  window.location.href = `/apply/${route.params.shortlinkId}`;
}

const shortlinkId = (route.params.shortlinkId ?? '').toString();
const shortlinkBaseUrl = config.public.baseUrl;

const { data } = await useAsyncData('vacancy', () =>
  $fetch<IGetVacancy>(config.public.apiTransportRoute as string, {
    method: 'GET',
    query: {
      ShortlinkId: shortlinkId,
      ShortlinkBaseUrl: shortlinkBaseUrl,
    },
  })
);

appStore.fetchData(data.value?.data);

useHead({
  title: appStore.apiData?.JobInfo?.JobTitle,
  htmlAttrs: {
    prefix: 'og: http://ogp.me/ns#',
    xmlns: 'http://www.w3.org/1999/xhtml',
    'xmlns:og': 'http://ogp.me/ns#',
    'xmlns:fb': 'http://www.facebook.com/2008/fbml',
  },
  meta: [
    {
      property: 'og:url',
      content: `${config.public.baseUrl}/vacancy/${
        useRouter().currentRoute.value.params.shortlinkId
      }`,
    },
    {
      property: 'og:image',
      name: 'twitter:image',
      // @ts-ignore
      itemprop: 'image primaryImageOfPage',
      content: appStore.apiData?.JobsiteSettings?.BannerImageUrl ?? '',
    },
    {
      property: 'og:title',
      name: 'twitter:title',
      // @ts-ignore
      itemprop: 'name',
      content: appStore.apiData?.JobInfo?.JobTitle,
    },
    {
      property: 'og:description',
      name: 'twitter:description',
      // @ts-ignore
      itemprop: 'description',
      content: appStore.apiData?.JobInfo?.Description,
    },
    {
      property: 'og:site_name',
      content: 'MiniSite',
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      name: 'twitter:card',
      content: appStore.apiData?.JobInfo?.Description,
    },
    {
      property: 'og:image:type',
      content: 'image/jpeg',
    },
    {
      property: 'og:image:width',
      content: '150',
    },
    {
      property: 'og:image:height',
      content: '150',
    },
    {
      property: 'twitter:image',
      content: appStore.apiData?.JobsiteSettings?.BannerImageUrl ?? '',
    },
    {
      property: 'twitter:title',
      content: appStore.apiData?.JobInfo?.JobTitle,
    },
    {
      property: 'twitter:description',
      content: appStore.apiData?.JobInfo?.Description,
    },
  ],
});
</script>
<template>
  <BaseLayout>
    <main class="font-sans h-full grow flex flex-col" :dir="appStore.dir">
      <div class="flex flex-col items-center grow">
        <div
          class="lg:relative flex flex-col justify-start grow items-stretch gap-[24px] lg:mt-[22px]"
        >
          <div class="relative">
            <SocialShare
              :title="appStore?.apiData?.JobInfo?.JobTitle ?? ''"
              :description="appStore?.apiData?.JobInfo?.Description ?? ''"
            />
          </div>
          <section
            v-if="appStore.apiData?.IsJobFound && appStore.apiData?.JobInfo"
            class="job-info"
          >
            <BaseTextBlock
              :title="LANG.description[appStore.lang]"
              :description="appStore.apiData?.JobInfo?.Description ?? ''"
              styles="mt-[27px] pt-[55px] lg:pt-[28px]"
              class="grow"
              :tabindex="10"
            />

            <BaseTextBlock
              v-if="(appStore?.apiData?.JobInfo?.Requirements?.length ?? 0) > 0"
              :title="LANG.requirements[appStore.lang]"
              :description="appStore.apiData?.JobInfo?.Requirements"
              class="grow"
              :tabindex="12"
            />

            <BaseTextBlock
              v-if="(appStore?.apiData?.JobInfo?.Skills?.length ?? 0) > 0"
              :title="LANG.skills[appStore.lang]"
              :description="appStore.apiData?.JobInfo?.Skills"
              class="grow"
              :tabindex="14"
            />
            <BaseButton
              @click="applyHandle"
              :text="LANG.applyFor[appStore.lang]"
              class="lg:min-w-[700px] min-w-[80vw]"
              color="#2C9EB3"
              :tabindex="15"
            />
          </section>
          <BaseAlert v-else :lang="appStore.lang" class="mt-8">
            <template #title>
              {{ LANG.jobError?.[appStore.lang] }}
            </template>
            {{
              LANG?.[
                !!appStore.apiData?.JobInfo ? 'jobUnavailiable' : 'jobInfoError'
              ]?.[appStore.lang]
            }}
          </BaseAlert>
        </div>
      </div>
      <ClientOnly>
        <BasePopup />
      </ClientOnly>
    </main>
  </BaseLayout>
</template>
<style>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
